/**
 * API 服务层
 * 封装与后端的所有 API 交互
 */
import axios from 'axios';

// 使用相对路径，利用React开发服务器的代理配置
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// 请求拦截器：添加认证 token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  // 跳过认证相关接口（注册、登录），这些接口不需要 token
  const authPaths = ['/api/auth/register', '/api/auth/token'];
  const isAuthPath = authPaths.some(path => config.url?.includes(path));
  
  if (token && !isAuthPath) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器：处理 token 过期
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * 认证相关 API
 */
export const authAPI = {
  /**
   * 用户注册
   * @param {object} user - 用户信息
   * @param {string} user.username - 用户名
   * @param {string} user.email - 邮箱
   * @param {string} user.password - 密码
   * @returns {Promise}
   */
  register: async (user) => {
    return await api.post('/api/auth/register', user);
  },

  /**
   * 用户登录
   * @param {string} username - 用户名
   * @param {string} password - 密码
   * @returns {Promise}
   */
  login: async (username, password) => {
    // 使用 URLSearchParams 来正确编码表单数据
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    return await api.post('/api/auth/token', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },

  /**
   * 获取当前用户信息
   * @returns {Promise}
   */
  getCurrentUser: async () => {
    return await api.get('/api/auth/me');
  },

  /**
   * 保存登录信息
   * @param {object} data - 登录响应数据
   */
  saveLoginInfo: (data) => {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('user', JSON.stringify({
      id: data.user_id,
      username: data.username,
      role: data.role || 'user',
    }));
  },

  /**
   * 清除登录信息
   */
  clearLoginInfo: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  },

  /**
   * 获取当前登录用户
   * @returns {object|null}
   */
  getCurrentUserFromStorage: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * 检查是否已登录
   * @returns {boolean}
   */
  isLoggedIn: () => {
    return !!localStorage.getItem('access_token');
  },
};

/**
 * 对话相关 API
 */
export const conversationAPI = {
  /**
   * 获取用户的所有对话
   * @returns {Promise}
   */
  list: async () => {
    return await api.get('/api/conversations');
  },

  /**
   * 创建新对话
   * @param {string} title - 对话标题
   * @returns {Promise}
   */
  create: async (title) => {
    return await api.post('/api/conversations', { title: title || '新对话' });
  },

  /**
   * 获取对话详情（包含消息）
   * @param {number} conversationId - 对话 ID
   * @returns {Promise}
   */
  get: async (conversationId) => {
    return await api.get(`/api/conversations/${conversationId}`);
  },

  /**
   * 更新对话标题
   * @param {number} conversationId - 对话 ID
   * @param {string} title - 新标题
   * @returns {Promise}
   */
  update: async (conversationId, title) => {
    console.log('API update conversation:', conversationId, title);
    const response = await api.put(`/api/conversations/${conversationId}`, { title });
    console.log('API update response:', response);
    return response;
  },

  /**
   * 更新对话置顶状态
   * @param {number} conversationId - 对话 ID
   * @param {boolean} isPinned - 是否置顶
   * @returns {Promise}
   */
  updatePin: async (conversationId, isPinned) => {
    return await api.put(`/api/conversations/${conversationId}/pin`, { is_pinned: isPinned });
  },

  /**
   * 删除对话
   * @param {number} conversationId - 对话 ID
   * @returns {Promise}
   */
  delete: async (conversationId) => {
    return await api.delete(`/api/conversations/${conversationId}`);
  },

  /**
   * 批量删除对话
   * @param {number[]} conversationIds - 对话 ID 列表
   * @returns {Promise}
   */
  batchDelete: async (conversationIds) => {
    return await api.post('/api/conversations/batch-delete', { conversation_ids: conversationIds });
  },

  /**
   * 发送消息
   * @param {number} conversationId - 对话 ID
   * @param {string} content - 消息内容
   * @returns {Promise}
   */
  sendMessage: async (conversationId, content) => {
    return await api.post(`/api/conversations/${conversationId}/messages`, { content });
  },
};

/**
 * 文档相关 API
 */
export const documentAPI = {
  /**
   * 上传文档
   * @param {File} file - 文档文件
   * @returns {Promise}
   */
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await api.post('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * 获取文档列表
   * @returns {Promise}
   */
  list: async () => {
    return await api.get('/api/documents');
  },

  /**
   * 获取单个文档详情
   * @param {number} id - 文档 ID
   * @returns {Promise}
   */
  get: async (id) => {
    return await api.get(`/api/documents/${id}`);
  },

  /**
   * 删除文档
   * @param {number} id - 文档 ID
   * @returns {Promise}
   */
  delete: async (id) => {
    return await api.delete(`/api/documents/${id}`);
  },
};

/**
 * 问答相关 API（保留兼容）
 */
export const qaAPI = {
  /**
   * 提问（无需认证）
   * @param {string} question - 用户问题
   * @returns {Promise}
   */
  ask: async (question) => {
    return await api.post('/api/qa', { question });
  },

  /**
   * 获取问答日志
   * @returns {Promise}
   */
  logs: async () => {
    return await api.get('/api/qa/logs');
  },
};

/**
 * 健康检查
 */
export const healthAPI = {
  check: async () => {
    return await api.get('/');
  },
};

export default api;
